import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Clock, Copy, Info, Loader2, Plus, Shield, ShieldOff, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCurrentUser } from '@/contexts/UserContext'
import { useNetwork } from '@/contexts/NetworkContext'
import { AuthService } from '@/services/core/auth'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { RolesApi } from '@/services/users/rolesApi'
import { TeamGroupsApi } from '@/services/users/teamGroupsApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { UsersApi, type NetworkUser, type RoleRef, type UserRole } from '@/services/users/usersApi'
import { draftToRoles, networkApps, rolesToDraft } from '@/services/users/userRoles'
import { localeLabel, USER_LOCALES } from '../../data/userLocales'
import { AppShell } from '../../components/AppShell'
import { CopyUserDialog } from '../../components/CopyUserDialog'
import { CurrencySelect } from '../../components/CurrencySelect'
import { DetailList, DetailRow } from '../../components/DetailList'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { SwitchField } from '../../components/SwitchField'
import { TwoFactorRecoveryDialog } from '../../components/TwoFactorRecoveryDialog'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { UserRolesEditor } from '../../components/UserRolesEditor'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate, formatDateTime } from '../../utils/format'

const BASE_PATH = '/admin/users'

/** The account only Adnuntius itself may change, whoever else can see it. */
const PROTECTED_USERNAME = 'adnuntius@adnuntius.com'

/** Sign-ins listed before the rest are behind "show all". */
const RECENT_SIGN_INS = 5

/** The user's roles in the network, whichever way the network is referenced. */
function rolesInNetwork(
  user: NetworkUser | null,
  network: { id: string; tagId: string } | null
): UserRole[] {
  if (!user || !network) return []
  return (
    user.userRoles?.rolesByNetwork?.find(
      (nr) => nr.network.tagId === network.tagId || nr.network.id === network.id
    )?.roles ?? []
  )
}

const toRoleRefs = (roles: UserRole[]): RoleRef[] =>
  roles.map((r) => ({
    role: r.role.id,
    ...(r.team ? { team: r.team.id } : {}),
    ...(r.teamGroup ? { teamGroup: r.teamGroup.id } : {}),
  }))

/** A sensible starting locale: the browser's, if it is one a user can pick. */
function defaultLocale(): string {
  const browser = (navigator.language || 'en-GB').replace('-', '_')
  if (USER_LOCALES.includes(browser)) return browser
  const language = browser.split('_')[0]
  return USER_LOCALES.includes(language) ? language : 'en_GB'
}

interface Catalog {
  roles: Awaited<ReturnType<typeof RolesApi.getAll>>
  teams: { id: string; name: string; description?: string; type?: string }[]
  teamGroups: { id: string; name: string; description?: string; type?: string }[]
}

// ── Inner form component ────────────────────────────────────────────────────

interface UserEditFormProps {
  user: NetworkUser | null
  catalog: Catalog
  id: string
  isNew: boolean
}

function UserEditForm({ user, catalog, id, isNew }: UserEditFormProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const { currentNetwork } = useNetwork()
  const [copyOpen, setCopyOpen] = useState(false)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const [allSignIns, setAllSignIns] = useState(false)

  const apps = useMemo(() => networkApps(currentNetwork), [currentNetwork])
  const networkRoles = rolesInNetwork(user, currentNetwork)
  const inNetwork = isNew || networkRoles.length > 0
  const isCurrentUser = currentUser?.id === id
  const blocked =
    user?.readOnly === true ||
    (user?.username === PROTECTED_USERNAME && currentUser?.username !== PROTECTED_USERNAME)

  const teamIds = useMemo(() => new Set(catalog.teams.map((team) => team.id)), [catalog.teams])
  const teamGroupIds = useMemo(
    () => new Set(catalog.teamGroups.map((group) => group.id)),
    [catalog.teamGroups]
  )

  const localeOptions = useMemo(
    () => USER_LOCALES.map((code) => ({ code, label: localeLabel(code, i18n.language) })),
    [i18n.language]
  )

  const { data: signIns = [] } = useQuery({
    queryKey: ['user-sign-ins', id],
    queryFn: () => UsersApi.getLoginStats(id),
    enabled: !isNew,
  })

  const form = useForm({
    defaultValues: {
      username: user?.username ?? '',
      name: user?.name ?? '',
      surname: user?.surname ?? '',
      displayName: user?.displayName ?? '',
      active: user ? user.objectState === 'ACTIVE' : true,
      locale: user?.locale && USER_LOCALES.includes(user.locale) ? user.locale : defaultLocale(),
      preferredCurrency: user?.preferredCurrency ?? '',
      externalReference: user?.externalReference ?? '',
      roles: rolesToDraft(networkRoles, apps, catalog.roles),
    },
    onSubmit: ({ value }) => mutation.mutate(value),
  })
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: async (value: typeof form.state.values) => {
      // Roles the editor does not show are sent back as they are now, so read
      // them fresh rather than trust what the page loaded.
      const fresh = isNew ? null : await UsersApi.getById(id)
      const roles = draftToRoles(
        value.roles,
        rolesInNetwork(fresh, currentNetwork),
        catalog.roles,
        teamIds,
        teamGroupIds
      )
      return UsersApi.save(
        id,
        {
          ...(isNew ? { username: value.username.trim() } : {}),
          name: value.name.trim(),
          surname: value.surname.trim(),
          displayName: value.displayName.trim(),
          locale: value.locale,
          preferredCurrency: value.preferredCurrency || null,
          externalReference: value.externalReference,
          objectState: value.active ? 'ACTIVE' : 'INACTIVE',
        },
        currentNetwork!.id,
        roles
      )
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['admin-user', id], data)
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['membership-users'] })
      // Refetched, not removed: the current user's query must keep its observer.
      if (isCurrentUser) queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      markClean()
      if (isNew) navigate(`${BASE_PATH}/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('adminUsers.createdSuccess') : t('adminUsers.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('adminUsers.createFailed')
            : t('adminUsers.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('nav.users'), href: BASE_PATH },
    { label: isNew ? t('users.newUser') : (user?.displayName ?? t('adminUsers.editTitle')) },
  ]

  const required = (value: string) => (value.trim() ? undefined : t('adminUsers.required'))

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.user')}>
      <form
        className="ad-page"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={user?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.user')} />
            <h1>
              <form.Field
                name="displayName"
                validators={{
                  onChange: ({ value }) => required(value),
                  onSubmit: ({ value }) => required(value),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('adminUsers.displayNamePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
            {user && <p>{user.username}</p>}
          </div>
          {user && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              {!blocked && (
                <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                  <Copy className="size-3.5" />
                  {t('copyUser.copy')}
                </Button>
              )}
            </div>
          )}
        </div>

        {user && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={user.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(user.createTime)}
                    updated={formatDate(user.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        {!inNetwork && (
          <div className="flex items-center gap-2.5 rounded-lg border border-(--ad-badge-blue-border) bg-(--ad-badge-blue-bg) px-3.5 py-2.5 text-[13px] text-(--ad-badge-blue)">
            <Info className="size-4 shrink-0" />
            {t('adminUsers.notInNetwork')}
          </div>
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={User} />
                  {t('users.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel
                      htmlFor="user-username"
                      tooltip={t('adminUsers.emailHelp')}
                      hint={isNew ? undefined : t('adminUsers.emailLocked')}
                    >
                      {t('users.email')}
                    </FieldLabel>
                    <form.Field
                      name="username"
                      validators={{
                        onSubmit: ({ value }) => (isNew ? required(value) : undefined),
                      }}
                    >
                      {(field) => (
                        <>
                          <Input
                            id="user-username"
                            type="email"
                            autoComplete="off"
                            value={field.state.value}
                            readOnly={!isNew}
                            disabled={blocked}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {field.state.meta.errors[0] && (
                            <span className="text-xs text-destructive">
                              {field.state.meta.errors[0] as string}
                            </span>
                          )}
                        </>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel htmlFor="user-name">{t('common.name')}</FieldLabel>
                    <form.Field
                      name="name"
                      validators={{ onSubmit: ({ value }) => required(value) }}
                    >
                      {(field) => (
                        <>
                          <Input
                            id="user-name"
                            autoComplete="off"
                            value={field.state.value}
                            disabled={blocked}
                            onChange={(e) => field.handleChange(e.target.value)}
                          />
                          {field.state.meta.errors[0] && (
                            <span className="text-xs text-destructive">
                              {field.state.meta.errors[0] as string}
                            </span>
                          )}
                        </>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel htmlFor="user-surname">{t('adminUsers.surname')}</FieldLabel>
                    <form.Field name="surname">
                      {(field) => (
                        <Input
                          id="user-surname"
                          autoComplete="off"
                          value={field.state.value}
                          disabled={blocked}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('adminUsers.localeHelp')}>
                      {t('users.locale')}
                    </FieldLabel>
                    <form.Field name="locale">
                      {(field) => (
                        <Select
                          label={t('users.locale')}
                          value={field.state.value}
                          disabled={blocked}
                          onValueChange={(v) => field.handleChange(v ?? '')}
                        >
                          <SelectTrigger>
                            <SelectValue>
                              {localeOptions.find((o) => o.code === field.state.value)?.label ??
                                field.state.value}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {localeOptions.map((o) => (
                              <SelectItem key={o.code} value={o.code}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel
                      tooltip={t('adminUsers.currencyHelp', {
                        currency: currentNetwork?.defaultCurrency ?? '',
                      })}
                    >
                      {t('adminUsers.preferredCurrency')}
                    </FieldLabel>
                    <form.Field name="preferredCurrency">
                      {(field) => (
                        <CurrencySelect
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v)}
                          fallback={currentNetwork?.defaultCurrency}
                          label={t('adminUsers.preferredCurrency')}
                          disabled={blocked}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <FieldLabel htmlFor="user-external-reference">
                      {t('teams.externalReference')}
                    </FieldLabel>
                    <form.Field name="externalReference">
                      {(field) => (
                        <Input
                          id="user-external-reference"
                          autoComplete="off"
                          value={field.state.value}
                          disabled={blocked}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <form.Field name="active">
                      {(field) => (
                        <SwitchField
                          label={t('common.active')}
                          description={
                            isCurrentUser ? t('adminUsers.cannotDeactivateSelf') : undefined
                          }
                          checked={field.state.value}
                          onCheckedChange={(v) => field.handleChange(v)}
                          disabled={blocked || (isCurrentUser && field.state.value)}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card tone="people">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Shield} />
                  {t('userRoles.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {apps.length ? (
                  <form.Field name="roles">
                    {(field) => (
                      <UserRolesEditor
                        apps={apps}
                        value={field.state.value}
                        onChange={(v) => field.handleChange(v)}
                        catalog={catalog.roles}
                        teams={catalog.teams}
                        teamGroups={catalog.teamGroups}
                        disabled={blocked}
                      />
                    )}
                  </form.Field>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('userRoles.noApps')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {user && !isNew && (
            <div className="flex min-w-0 flex-1 flex-col gap-3.5">
              <Card tone="insight">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Clock} />
                    {t('adminUsers.signIn')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DetailList>
                    <DetailRow label={t('users.lastLogin')}>
                      {user.lastLogin ? formatDateTime(user.lastLogin) : t('adminUsers.never')}
                    </DetailRow>
                    <DetailRow label={t('users.lastPasswordChange')}>
                      {user.lastPasswordChange ? formatDateTime(user.lastPasswordChange) : '—'}
                    </DetailRow>
                    <DetailRow label={t('users.twoFactor')}>
                      {user.login2faRequired
                        ? t('adminUsers.twoFactorRequired')
                        : user.twoFactorAuth
                          ? t('adminUsers.twoFactorEnabled')
                          : t('adminUsers.twoFactorOff')}
                    </DetailRow>
                  </DetailList>
                  {user.login2faRequired && !blocked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setRecoveryOpen(true)}
                    >
                      <ShieldOff className="size-3.5" />
                      {t('twoFactorRecovery.title')}
                    </Button>
                  )}
                  {signIns.length > 0 && (
                    <div className="mt-4 flex flex-col gap-1.5">
                      <FieldLabel>{t('adminUsers.recentSignIns')}</FieldLabel>
                      <ul className="flex flex-col gap-0.5 text-[13px] text-(--ad-text-muted)">
                        {(allSignIns ? signIns : signIns.slice(0, RECENT_SIGN_INS)).map(
                          (signIn, index) => (
                            <li key={`${signIn.dateTime}-${index}`}>
                              {formatDateTime(signIn.dateTime)}
                            </li>
                          )
                        )}
                      </ul>
                      {signIns.length > RECENT_SIGN_INS && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto self-start p-0"
                          onClick={() => setAllSignIns((v) => !v)}
                        >
                          {allSignIns
                            ? t('adminUsers.showFewerSignIns')
                            : t('adminUsers.showAllSignIns', { count: signIns.length })}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {!blocked && (
          <div
            className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
          >
            <div className="detail-savebar__left">
              {mutation.isPending && (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {isNew ? t('common.creating') : t('common.saving')}
                </>
              )}
              {isNew && !mutation.isPending && t('adminUsers.newUserEmail')}
            </div>
            <div className="detail-savebar__actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(BASE_PATH)}
                disabled={mutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
                {isNew ? t('adminUsers.createUser') : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </form>
      <UnsavedChangesDialog blocker={blocker} />
      {user && currentNetwork && copyOpen && (
        <CopyUserDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          user={user}
          roles={toRoleRefs(networkRoles)}
          networkId={currentNetwork.id}
          basePath={BASE_PATH}
        />
      )}
      {user && (
        <TwoFactorRecoveryDialog open={recoveryOpen} onOpenChange={setRecoveryOpen} userId={id} />
      )}
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function AdminUserEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()
  const { currentNetwork } = useNetwork()
  const advertising = !!currentNetwork?.advertisingPlatform

  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => UsersApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  // The role editor starts from these, so the form waits for them.
  const { data: catalog, isError: catalogError } = useQuery({
    queryKey: ['user-role-catalog', currentNetwork?.tagId, advertising],
    queryFn: async (): Promise<Catalog> => {
      const [roles, teams, teamGroups] = await Promise.all([
        RolesApi.getAll(),
        TeamsApi.getAll(),
        advertising ? TeamGroupsApi.getAllPages(undefined, 'ACTIVE') : Promise.resolve([]),
      ])
      return {
        roles,
        teams: teams.map(({ id, name, description, type }) => ({ id, name, description, type })),
        teamGroups: teamGroups.map(({ id, name, description, type }) => ({
          id,
          name,
          description,
          type,
        })),
      }
    },
    enabled: hasToken && !!currentNetwork,
  })

  const crumbsLoading = [
    { label: t('nav.users'), href: BASE_PATH },
    { label: isNew ? t('users.newUser') : t('adminUsers.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (catalogError) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <div className="ad-page">
          <p className="text-sm text-destructive">{t('adminUsers.rolesLoadFailed')}</p>
        </div>
      </AppShell>
    )
  }

  if ((isLoading && !isNew) || !catalog) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <UserEditForm key={id} user={user ?? null} catalog={catalog} id={id!} isNew={isNew} />
}
